import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';
const DATASET='sec-companyfacts';

function parseCompanyFacts(json,symbol){
  if(!json||typeof json!=='object') throw Error('SEC_PAYLOAD_REQUIRED');
  const facts=json.facts||{};
  const out={entityName:json.entityName||null,cik:json.cik||null,symbol:symbol||null,facts:{}};
  for(const taxonomy of Object.keys(facts)){
    for(const [name,fact] of Object.entries(facts[taxonomy]||{})){
      const units=fact&&fact.units||{};
      const rows=[];
      for(const [unit,values] of Object.entries(units)){
        for(const v of Array.isArray(values)?values:[]){
          rows.push({
            taxonomy,name,label:fact.label||name,unit,
            value:v.val??null,start:v.start||null,end:v.end||null,
            filed:v.filed||null,form:v.form||null,fy:v.fy??null,fp:v.fp||null,
            accession:v.accn||null,frame:v.frame||null
          });
        }
      }
      if(rows.length) out.facts[name]=rows;
    }
  }
  return out;
}

function latestFiled(parsed){
  let latest=null;
  for(const rows of Object.values(parsed.facts||{})){
    for(const r of rows){
      if(r.filed && (!latest||r.filed>latest)) latest=r.filed;
    }
  }
  return latest;
}

function create(opts={}){
  const fetchFn=opts.fetchFn||g.fetch;
  const userAgent=opts.userAgent||null;
  const endpoint=opts.endpoint||'https://data.sec.gov/api/xbrl/companyfacts/CIK{CIK}.json';

  async function fetchCompanyFacts(req={}){
    const cik=String(req.cik||'').replace(/\D/g,'').padStart(10,'0');
    if(!/^\d{10}$/.test(cik)||cik==='0000000000') throw Error('VALID_CIK_REQUIRED');
    if(typeof fetchFn!=='function') throw Error('FETCH_REQUIRED');

    const url=endpoint.replace('{CIK}',cik);
    const headers={Accept:'application/json'};
    if(userAgent) headers['User-Agent']=userAgent;

    const res=await fetchFn(url,{headers});
    if(!res||!res.ok) throw Error(`SEC_HTTP_${res&&res.status||'ERROR'}`);
    const json=await res.json();
    const parsed=parseCompanyFacts(json,req.symbol||null);
    const asOf=latestFiled(parsed);

    return {
      symbol:req.symbol||null,
      asOf,
      receivedAt:new Date().toISOString(),
      state:asOf?'VERIFIED':'UNVERIFIED',
      provenance:{
        provider:'SEC',
        dataset:DATASET,
        endpoint:url,
        cik,
        filingBasis:'SEC Company Facts'
      },
      payload:parsed
    };
  }

  return Object.freeze({
    name:'SEC',
    datasets:Object.freeze([DATASET]),
    fetch:fetchCompanyFacts
  });
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const fixture={
    cik:320193,entityName:'Example Corp',
    facts:{'us-gaap':{
      Revenues:{label:'Revenue',units:{USD:[
        {val:90,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY',accn:'1'},
        {val:100,end:'2026-03-31',filed:'2026-05-01',form:'10-Q',fy:2026,fp:'Q1',accn:'2'}
      ]}},
      Assets:{label:'Assets',units:{USD:[
        {val:500,end:'2026-03-31',filed:'2026-05-01',form:'10-Q',fy:2026,fp:'Q1',accn:'2'}
      ]}}
    }}
  };

  const p=parseCompanyFacts(fixture,'AAA');
  ok('entity name parsed',p.entityName==='Example Corp');
  ok('cik parsed',p.cik===320193);
  ok('symbol preserved',p.symbol==='AAA');
  ok('revenue facts parsed',p.facts.Revenues.length===2);
  ok('asset facts parsed',p.facts.Assets.length===1);
  ok('unit preserved',p.facts.Revenues[0].unit==='USD');
  ok('form preserved',p.facts.Revenues[1].form==='10-Q');
  ok('latest filing detected',latestFiled(p)==='2026-05-01');

  let requested='';
  const adapter=create({fetchFn:async url=>{requested=url;return {ok:true,status:200,json:async()=>fixture}}});
  ok('adapter name SEC',adapter.name==='SEC');
  ok('dataset declared',adapter.datasets[0]===DATASET);

  const r=await adapter.fetch({cik:'320193',symbol:'AAA'});
  ok('cik padded in endpoint',requested.includes('CIK0000320193.json'));
  ok('verified when filing date exists',r.state==='VERIFIED');
  ok('asOf uses latest filed date',r.asOf==='2026-05-01');
  ok('provenance identifies SEC',r.provenance.provider==='SEC');
  ok('payload carries facts',r.payload.facts.Revenues.length===2);

  const noFacts=create({fetchFn:async()=>({ok:true,status:200,json:async()=>({cik:1,entityName:'X',facts:{}})})});
  const nf=await noFacts.fetch({cik:1,symbol:'X'});
  ok('empty facts cannot claim verified',nf.state==='UNVERIFIED');
  ok('empty facts has no asOf',nf.asOf===null);

  const badHttp=create({fetchFn:async()=>({ok:false,status:429})});
  let he=false;try{await badHttp.fetch({cik:1})}catch(e){he=e.message==='SEC_HTTP_429'}
  ok('http error explicit',he);

  let ce=false;try{await adapter.fetch({cik:'abc'})}catch(e){ce=e.message==='VALID_CIK_REQUIRED'}
  ok('invalid cik rejected',ce);

  let pe=false;try{parseCompanyFacts(null)}catch(e){pe=e.message==='SEC_PAYLOAD_REQUIRED'}
  ok('invalid payload rejected',pe);

  const api=create({fetchFn:async()=>({ok:true,json:async()=>fixture})});
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestSECCompanyFactsProviderAdapter=Object.freeze({VERSION,DATASET,parseCompanyFacts,latestFiled,create,selfTest});
})(engineContext);